import type {
    AiTrackerReportsBrandOverviewBrandMetrics,
    AiTrackerReportsBrandOverviewBrandVisibilityData,
} from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import { assignDynamicLegendColors } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

interface ILegendLikeItem {
    id: string;
    name: string;
    color: string;
    visible: boolean;
    isCoreAverage?: boolean;
    isDisabled?: boolean;
}

export type CompareChartMetrics = Pick<AiTrackerReportsBrandOverviewBrandMetrics, "visibility" | "sentiment">;
export type CompareChartViewMode = keyof CompareChartMetrics;
export const CORE_AVERAGE_COLOR = colorsPalettes.carbon[200];
export const CORE_AVERAGE_ID_SUFFIX = "core-average";

interface ICoreAverageSeriesResult {
    id: string;
    name: string;
    color: string;
    data: number[][];
    total: number;
    visible: boolean;
    dashStyle: "Dash";
    isCoreAverage: true;
    isDisabled?: boolean;
}

const buildCoreAverageSeriesBase = (id: string, total: number, data: number[][]): ICoreAverageSeriesResult => ({
    id,
    name: translate("llm.brands-visibility.core_average.legend"),
    color: CORE_AVERAGE_COLOR,
    total,
    data,
    visible: true,
    dashStyle: "Dash",
    isCoreAverage: true,
    isDisabled: true,
});

export const hasCoreCompetitors = (
    coreAverage: AiTrackerReportsBrandOverviewBrandVisibilityData | undefined,
): boolean => !!coreAverage && (coreAverage.daily !== undefined || coreAverage.weekly !== undefined);

export const buildCoreAverageSeries = (
    topicName: string,
    coreAverage: AiTrackerReportsBrandOverviewBrandVisibilityData | undefined,
    granularity: GranularityNames,
): ICoreAverageSeriesResult | null => {
    if (!hasCoreCompetitors(coreAverage)) {
        return null;
    }

    const dataMap: Record<string, number> =
        granularity === GranularityNames.WEEKLY ? (coreAverage?.weekly ?? {}) : (coreAverage?.daily ?? {});

    return buildCoreAverageSeriesBase(
        `${topicName}-${CORE_AVERAGE_ID_SUFFIX}`,
        coreAverage?.total ?? 0,
        Object.keys(dataMap).map((date) => [new Date(date).getTime(), dataMap[date]]),
    );
};

export const buildCompareCoreAverageSeries = (
    viewMode: CompareChartViewMode,
    topicName: string,
    coreAverage: CompareChartMetrics | undefined,
    modelCount: number,
): ICoreAverageSeriesResult | null => {
    if (!coreAverage) {
        return null;
    }

    return buildCoreAverageSeriesBase(
        `${viewMode}-${topicName}-${CORE_AVERAGE_ID_SUFFIX}`,
        coreAverage[viewMode] ?? 0,
        Array.from({ length: modelCount }, (_, index) => [index, 0]),
    );
};

/**
 * Recomputes `visible` for each brand in a topic based on the legend selection,
 * skipping the core-average item (which is pinned and always visible).
 */
export const updateBrandsVisibility = <T extends ILegendLikeItem>(
    brands: T[],
    {
        isCurrentTopic,
        selectedIds,
        changedBrands,
    }: {
        isCurrentTopic: boolean;
        selectedIds: Set<string>;
        changedBrands: Map<string, boolean>;
    },
): T[] =>
    brands.map((brand) => {
        if (brand.isCoreAverage) {
            return brand;
        }

        const shouldBeVisible = isCurrentTopic
            ? selectedIds.has(brand.id)
            : (changedBrands.get(brand.name) ?? brand.visible);

        return isCurrentTopic || changedBrands.has(brand.name) ? { ...brand, visible: shouldBeVisible } : brand;
    });

/**
 * Enforces a MAX cap on the total number of visible items per topic. A selectable
 * core-average item is pinned and counts toward MAX; a disabled core-average is
 * informational only and does not reduce the brand cap.
 */
export const capVisibleBrands = <T extends ILegendLikeItem>(brands: T[], maxSelected: number): T[] => {
    const hasSelectableCoreAverage = brands.some(
        (brand) => brand.isCoreAverage && brand.visible && !brand.isDisabled,
    );
    const regularBrandsCap = hasSelectableCoreAverage ? maxSelected - 1 : maxSelected;
    let selectedCount = 0;

    return brands.map((brand) => {
        if (brand.isCoreAverage) {
            return brand;
        }

        if (brand.visible && selectedCount < regularBrandsCap) {
            selectedCount++;
            return brand;
        }

        return brand.visible ? { ...brand, visible: false } : brand;
    });
};

/**
 * Reassigns dynamic legend colors to real brands while keeping the core-average
 * item (if present) appended at the bottom with its original color.
 */
export const recolorPreservingCoreAverage = <T extends ILegendLikeItem>(brands: T[]): T[] => {
    const realBrands = brands.filter((b) => !b.isCoreAverage);
    const coreAverage = brands.find((b) => b.isCoreAverage);
    const recolored = assignDynamicLegendColors(realBrands);

    return coreAverage ? [...recolored, coreAverage] : recolored;
};
